import { useState } from "react";
import createDOMPurify from "dompurify";
import { useNavigate } from "react-router-dom";
import { FaEdit, FaPause, FaPlay, FaTrashAlt } from "react-icons/fa";
import { AiOutlineLoading } from "react-icons/ai";

import { MyButton } from "../../component/ButtonUtils";
import { pushCountry } from "../../../../middlewares/sysNavigation/SysNavigation";
import { useDoSeeDataContext } from "../../../../context/doSeeData/DoSeeDataContext";
import { useAdminContext } from "../../../../context/admin/context/AdminContext";
import { getType } from "../contentStats/helper";

const DOMPurify = createDOMPurify(window);

export const addTextNewLines = (val = "") => {
  return (
    <div
      className="line-clamp-3"
      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(val) }}
    />
  );
};

export const SeachContent = ({
  data,
  route,
  title,
  idLabel,
  setLocalSearch,
  titleLabel = "title",
  collection,
  type,
  pushMethod,
  onDeleteAction,
}) => {
  return (
    <div className={data && data?.length !== 0 ? "block w-full space-y-2" : "hidden"}>
      <div className="flex items-center justify-between">
        <p className="text-[13px] font-semibold uppercase tracking-wide text-gray-700">
          {title}
        </p>
        <span className="rounded bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-900">
          {data?.length}
        </span>
      </div>

      <div className="flex flex-col space-y-4">
        {data?.map((item, idx) => (
          <SeachListItem
            key={idx}
            index={idx + 1}
            route={route}
            id={item[`${idLabel}`]}
            title={item[`${titleLabel}`]}
            setLocalSearch={setLocalSearch}
            description={item?.description}
            status={item?.status}
            collection={collection}
            type={type}
            pushMethod={pushMethod}
            content={item}
            onDeleteAction={onDeleteAction}
          />
        ))}
      </div>
    </div>
  );
};

const SeachListItem = ({
  id,
  index,
  title,
  route,
  description,
  setLocalSearch,
  collection,
  type,
  pushMethod,
  content,
  status,
  onDeleteAction,
}) => {
  const navigate = useNavigate();
  const { deleteOneContent, updateOneContentStatus } = useDoSeeDataContext();
  const { sysAdmin, setSingleAdmin } = useAdminContext();

  const [contentStatus, setContentStatus] = useState(status);
  const [loading, setLoading] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(false);

  // Confirmation modal state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmType, setConfirmType] = useState(null); // 'delete' | 'edit' | 'toggle'

  const isActive = contentStatus === 1;

  // Original actions (unchanged logic)
  const doDelete = async () => {
    setLoading(true);
    const { status } = await deleteOneContent({ data_id: id, content: collection });
    setLoading(false);
    if (status === 200) {
      setLocalSearch(null);
      onDeleteAction();
    }
  };

  const doEdit = () => {
    pushMethod(content);
    pushCountry({
      country: { id: 0, name: "", slug: "", route: "/search" },
    });
    setSingleAdmin({
      ids: [sysAdmin?.admin_id],
      type: getType({ field: collection }),
      route: "/search",
    });
    navigate(route);
  };

  const doToggleStatus = async () => {
    setLoadingStatus(true);
    const statusState = isActive ? 0 : 1;
    const { status } = await updateOneContentStatus({ id, type, statusState });
    setLoadingStatus(false);
    if (status === 200) setContentStatus(statusState);
  };

  // Open confirm for a given type
  const openConfirm = (type) => {
    setConfirmType(type);
    setConfirmOpen(true);
  };

  // Handle confirmation
  const handleConfirm = async () => {
    if (confirmType === "delete") await doDelete();
    if (confirmType === "edit") doEdit();
    if (confirmType === "toggle") await doToggleStatus();
    setConfirmOpen(false);
    setConfirmType(null);
  };

  return (
    <>
      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition hover:shadow-md">
        <div className="mb-2 flex items-start justify-between">
          <p className="text-sm font-semibold text-gray-900">
            {index}. {title}
          </p>
          <span
            className={[
              "rounded-full px-2.5 py-1 text-[11px] font-semibold",
              isActive
                ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
                : "bg-orange-50 text-orange-700 ring-1 ring-orange-200",
            ].join(" ")}
          >
            {isActive ? "Active" : "Suspended"}
          </span>
        </div>

        <div
          className="prose prose-sm max-w-none text-xs text-gray-700 line-clamp-4"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(description) }}
        />

        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3">
          {/* DELETE */}
          <MyButton
            onClick={() => openConfirm("delete")}
            className="inline-flex items-center gap-2 rounded-lg bg-gray-900 px-3 py-2 text-xs font-semibold text-white hover:bg-gray-800"
          >
            {loading ? (
              <AiOutlineLoading className="animate-spin" />
            ) : (
              <>
                <FaTrashAlt className="text-white" />
                Delete
              </>
            )}
          </MyButton>

          {/* EDIT (now confirmed, then navigate programmatically) */}
          <MyButton
            onClick={() => openConfirm("edit")}
            className="inline-flex items-center gap-2 rounded-lg bg-gray-900 px-3 py-2 text-xs font-semibold text-white hover:bg-gray-800"
          >
            <FaEdit className="text-white" />
            Edit
          </MyButton>

          {/* TOGGLE STATUS */}
          <MyButton
            onClick={() => openConfirm("toggle")}
            className={[
              "inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold text-white",
              isActive ? "bg-red-700 hover:bg-red-700/90" : "bg-orange-600 hover:bg-orange-600/90",
            ].join(" ")}
          >
            {loadingStatus ? (
              <AiOutlineLoading className="animate-spin" />
            ) : isActive ? (
              <>
                <FaPause className="text-white" />
                Suspend
              </>
            ) : (
              <>
                <FaPlay className="text-white" />
                Unsuspend
              </>
            )}
          </MyButton>
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmModal
        open={confirmOpen}
        onCancel={() => {
          setConfirmOpen(false);
          setConfirmType(null);
        }}
        onConfirm={handleConfirm}
        type={confirmType}
        itemTitle={title}
        isActive={isActive}
        loading={confirmType === "delete" ? loading : confirmType === "toggle" ? loadingStatus : false}
      />
    </>
  );
};

/** Clean, modern confirmation modal */
const ConfirmModal = ({ open, onCancel, onConfirm, type, itemTitle, isActive, loading }) => {
  if (!open) return null;

  const tone = type === "delete" ? "danger" : type === "toggle" ? (isActive ? "warning" : "primary") : "primary";
  const palette = {
    danger: {
      icon: "text-rose-600",
      ring: "ring-rose-100",
      header: "text-rose-800",
      badge: "bg-rose-50 text-rose-700 ring-1 ring-rose-100",
      confirmBtn: "bg-rose-600 hover:bg-rose-700 focus-visible:ring-rose-500",
    },
    warning: {
      icon: "text-amber-600",
      ring: "ring-amber-100",
      header: "text-amber-800",
      badge: "bg-amber-50 text-amber-700 ring-1 ring-amber-100",
      confirmBtn: "bg-amber-600 hover:bg-amber-700 focus-visible:ring-amber-500",
    },
    primary: {
      icon: "text-emerald-600",
      ring: "ring-emerald-100",
      header: "text-emerald-800",
      badge: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100",
      confirmBtn: "bg-emerald-600 hover:bg-emerald-700 focus-visible:ring-emerald-500",
    },
  }[tone];

  const titleMap = {
    delete: "Delete this content?",
    edit: "Open this content for editing?",
    toggle: isActive ? "Suspend this content?" : "Unsuspend this content?",
  };

  const descMap = {
    delete:
      "This action will permanently remove the content. This cannot be undone.",
    edit:
      "You’re about to open the editor for this content. Unsaved changes elsewhere may be lost.",
    toggle: isActive
      ? "This will make the content unavailable to users until reactivated."
      : "This will make the content available to users.",
  };

  const confirmTextMap = {
    delete: "Yes, delete",
    edit: "Yes, open",
    toggle: isActive ? "Yes, suspend" : "Yes, unsuspend",
  };

  return (
    <div className="fixed inset-0 z-[1000] min-h-screen">
      {/* Backdrop */}
      <div
        className="absolute min-h-screen inset-0 bg-black/40 backdrop-blur-[1px]"
        onClick={onCancel}
      />

      {/* Dialog */}
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className={`w-full max-w-md rounded-2xl bg-white p-6 shadow-xl ring-1 ${palette.ring}`}>
          {/* Icon */}
          <div className="mb-3 flex items-center justify-center">
            <svg
              className={`h-10 w-10 ${palette.icon}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>

          {/* Title */}
          <h3 className={`text-center text-base font-semibold ${palette.header}`}>{titleMap[type]}</h3>

          {/* Item */}
          <div className="mt-2 text-center">
            <span className={`inline-block rounded-full px-2 py-1 text-[11px] font-semibold ${palette.badge}`}>
              {itemTitle}
            </span>
          </div>

          {/* Description */}
          <p className="mt-3 text-center text-xs text-gray-600">{descMap[type]}</p>

          {/* Actions */}
          <div className="mt-6 flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white px-4 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-300"
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={onConfirm}
              className={`inline-flex items-center justify-center rounded-lg px-4 py-2 text-xs font-semibold text-white focus:outline-none focus-visible:ring-2 ${palette.confirmBtn}`}
            >
              {loading ? <AiOutlineLoading className="animate-spin" /> : confirmTextMap[type]}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
